/**
 * Plain HTML table for Herald chat spec renderer. Accepts columns and rows, or
 * data + optional initial sortKey/sortDir. Uses aiTableUtils for sorting;
 * column headers are clickable to toggle sort.
 */
import {
  ArrowDownIcon,
  ArrowsDownUpIcon,
  ArrowUpIcon,
} from "@phosphor-icons/react";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from "@/components/ui/card/card";
import ChatLinkableText from "@/components/ui/chat/ChatLinkableText";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useOptionalHeraldCardCatalogTableRegistration } from "@/lib/chat/heraldCardCatalogTableRegistrationContext";
import { cn } from "@/lib/utils";
import { sortTableItems, tableCellToString } from "@/lib/utils/aiTableUtils";

/**
 * Column headers that indicate positive/negative/neutral classification cells.
 * Uses word boundaries so substrings like "tone" inside unrelated words do not match.
 */
const isSentimentStyleColumn = (columnHeader: string): boolean => {
  const h = columnHeader.trim();
  if (
    /^(headline|title|url|link|articles?|publisher|source|name|handle)$/i.test(
      h,
    )
  ) {
    return false;
  }
  return (
    /\b(sentiment|stance|polarity|verdict|triage)\b/i.test(h) ||
    /\btones?\b/i.test(h) ||
    /risk\s*direction/i.test(h)
  );
};

type SentimentBadgeVariant = "default" | "destructive" | "secondary";

const sentimentVariantFromCellValue = (
  raw: string,
): SentimentBadgeVariant | null => {
  const v = raw.trim().toLowerCase();
  if (
    v === "positive" ||
    v === "supportive" ||
    v === "bullish" ||
    v === "pro"
  ) {
    return "default";
  }
  if (v === "negative" || v === "critical" || v === "bearish" || v === "con") {
    return "destructive";
  }
  if (v === "neutral" || v === "mixed" || v === "balanced") {
    return "secondary";
  }
  return null;
};

const sentimentPillClass: Record<SentimentBadgeVariant, string> = {
  default: "bg-primary text-primary-foreground",
  destructive: "bg-destructive text-destructive-foreground",
  secondary: "bg-secondary text-secondary-foreground",
};

const tableDataTextClass = "text-[13px]";

const HeraldInsideCatalogCardContext = createContext(false);

/**
 * Marks descendants so {@link ChatTable} omits its own Card when nested under
 * a Herald catalog `Card`.
 */
export const HeraldCatalogCardSubtree = ({
  children,
}: {
  children: ReactNode;
}) => (
  <HeraldInsideCatalogCardContext.Provider value={true}>
    {children}
  </HeraldInsideCatalogCardContext.Provider>
);

/** True when the subtree is rendered under a Herald catalog `Card` (see {@link HeraldCatalogCardSubtree}). */
export const useHeraldInsideCatalogCard = (): boolean =>
  useContext(HeraldInsideCatalogCardContext);

const TableCellContent = ({
  columnHeader,
  raw,
}: {
  columnHeader: string;
  raw: unknown;
}) => {
  const text = tableCellToString(raw);
  const variant = isSentimentStyleColumn(columnHeader)
    ? sentimentVariantFromCellValue(text)
    : null;
  if (variant != null) {
    return (
      <span
        className={cn(
          "inline-flex max-w-full min-w-0 items-center overflow-hidden rounded-full px-2 py-0.5 font-medium text-ellipsis whitespace-nowrap",
          tableDataTextClass,
          sentimentPillClass[variant],
        )}
      >
        {text}
      </span>
    );
  }
  return <ChatLinkableText value={raw} className={tableDataTextClass} />;
};

export interface ChatTableProps {
  columns?: string[] | null;
  rows?: string[][];
  /** Main heading above the table (bold). */
  title?: string | null;
  /** Muted line under the heading (e.g. date range). */
  subtitle?: string | null;
  /** Shown as the bold heading if `title` is omitted (same role as title). */
  caption?: string | null;
  /** When provided, rows are derived by sorting with aiTableUtils. */
  data?: Record<string, unknown>[];
  /** Initial sort column (user can change via header click). */
  sortKey?: string | null;
  /** Initial sort direction. */
  sortDir?: "asc" | "desc";
  /** Message when there is no data. */
  emptyMessage?: string | null;
}

/**
 * Converts rows (string[][]) and columns into an array of records for sorting.
 */
const rowsToData = (
  columns: string[],
  rows: string[][],
): Record<string, unknown>[] =>
  rows.map((row) => {
    const obj: Record<string, unknown> = {};
    columns.forEach((col, i) => {
      obj[col] = row[i] ?? "";
    });
    return obj;
  });

const TableHeading = ({
  title,
  subtitle,
}: {
  title?: string | null;
  subtitle?: string | null;
}) => {
  if (!title && !subtitle) return null;
  return (
    <div className="mb-8 grid gap-1 px-3 pt-2 text-left">
      {title ? <CardTitle>{title}</CardTitle> : null}
      {subtitle ? <CardDescription>{subtitle}</CardDescription> : null}
    </div>
  );
};

const ChatTable = ({
  columns: columnsProp,
  rows: rowsProp,
  title,
  subtitle,
  caption,
  data: dataProp,
  sortKey: initialSortKey,
  sortDir: initialSortDir,
  emptyMessage = "No data",
}: ChatTableProps) => {
  const insideCatalogCard = useHeraldInsideCatalogCard();
  const cardCatalogTableRegistration =
    useOptionalHeraldCardCatalogTableRegistration();

  useEffect(() => {
    if (!(insideCatalogCard && cardCatalogTableRegistration)) {
      return;
    }
    cardCatalogTableRegistration.registerCatalogTable();
    return () => {
      cardCatalogTableRegistration.unregisterCatalogTable();
    };
  }, [insideCatalogCard, cardCatalogTableRegistration]);

  const columns = Array.isArray(columnsProp) ? columnsProp : [];

  const data = useMemo(() => {
    if (Array.isArray(dataProp) && dataProp.length > 0) return dataProp;
    if (Array.isArray(rowsProp) && rowsProp.length > 0) {
      return rowsToData(columns, rowsProp);
    }
    return [];
  }, [dataProp, rowsProp, columns]);

  const [sortKey, setSortKey] = useState<string | null>(initialSortKey ?? null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">(
    initialSortDir ?? "asc",
  );

  const sorted = useMemo(
    () => sortTableItems(data, sortKey, sortDir),
    [data, sortKey, sortDir],
  );

  const headingTitle = title ?? caption ?? null;
  const headingSubtitle = subtitle ?? null;

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const emptyInner = (
    <div className="w-full overflow-hidden">
      {headingTitle || headingSubtitle ? (
        <TableHeading title={headingTitle} subtitle={headingSubtitle} />
      ) : null}
      <div className="px-3 py-4 text-center text-sm text-gray-400">
        {/* Fallback for when spec passes null (Zod nullable()) */}
        {emptyMessage ?? "No data"}
      </div>
    </div>
  );

  const tableInner = (
    <div className="relative w-full overflow-hidden">
      {headingTitle || headingSubtitle ? (
        <TableHeading title={headingTitle} subtitle={headingSubtitle} />
      ) : null}
      <Table className="text-foreground">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {columns.map((col) => {
              const isSorted = sortKey === col;
              const ariaSort = isSorted
                ? sortDir === "asc"
                  ? "ascending"
                  : "descending"
                : "none";
              return (
                <TableHead
                  key={col}
                  aria-sort={ariaSort}
                  className={cn(
                    "h-auto px-3 py-2 text-gray-900 whitespace-normal",
                    tableDataTextClass,
                  )}
                >
                  <button
                    type="button"
                    className={cn(
                      "inline-flex max-w-full min-w-0 items-start gap-1 text-left text-gray-900 transition-colors",
                      tableDataTextClass,
                    )}
                    onClick={() => handleSort(col)}
                  >
                    <span className="min-w-0 whitespace-normal break-words">
                      {col}
                    </span>
                    {isSorted ? (
                      sortDir === "asc" ? (
                        <ArrowUpIcon className="shrink-0 text-gray-400 size-3" />
                      ) : (
                        <ArrowDownIcon className="shrink-0 text-gray-400 size-3" />
                      )
                    ) : (
                      <ArrowsDownUpIcon className="shrink-0 text-gray-400 size-3" />
                    )}
                  </button>
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((item, i) => {
            const rowKey = `${columns.map((col) => tableCellToString(item[col])).join("|")}|${i}`;
            return (
              <TableRow key={rowKey}>
                {columns.map((col) => (
                  <TableCell
                    key={col}
                    className={cn(
                      "px-3 py-2 text-gray-900 whitespace-normal",
                      tableDataTextClass,
                    )}
                  >
                    <TableCellContent columnHeader={col} raw={item[col]} />
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );

  if (data.length === 0) {
    return insideCatalogCard ? (
      <div className="relative w-full min-w-0 overflow-hidden">
        {emptyInner}
      </div>
    ) : (
      <Card size="sm" className="gap-0 py-0">
        <CardContent className="p-0">{emptyInner}</CardContent>
      </Card>
    );
  }

  return insideCatalogCard ? (
    <div className="relative w-full min-w-0 overflow-hidden">{tableInner}</div>
  ) : (
    <Card size="sm" className="relative gap-0 py-0">
      <CardContent className="p-0">{tableInner}</CardContent>
    </Card>
  );
};

export default ChatTable;
